import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { 
  TrendingUp, Truck, DollarSign, FileText, AlertCircle, 
  Plus, Mail, Users, ArrowRight, Clock, CheckCircle, 
  Package, Building2, Receipt, Send, Loader2
} from 'lucide-react';
import DateRangePicker, { getDateRange } from '../components/DateRangePicker';
import { useCompany } from '../contexts/CompanyContext';
import { RecentLoadRow } from './dashboard/RecentLoadRow';
import { AgentMarginPanel } from './dashboard/AgentMarginPanel';
import { MyEmailPerformanceWidget } from './dashboard/MyEmailPerformanceWidget';
import { useMarginViewMode } from '../hooks/useMarginViewMode';
import { MarginViewToggle } from '../components/analytics/MarginViewToggle';
import { MarginGoalBar } from '../components/analytics/MarginGoalBar';

export default function Dashboard() {
  const { mode: marginMode, setMode: setMarginMode, includePipeline: marginIncludesPipeline } = useMarginViewMode();
  const [myGoal, setMyGoal] = useState(null);
  const [myTeamGoal, setMyTeamGoal] = useState(null);
  const { token, API, user } = useAuth();
  const company = useCompany();
  const [stats, setStats] = useState(null);
  const [recentLoads, setRecentLoads] = useState([]);
  const [parsedEmails, setParsedEmails] = useState(0);
  const [loading, setLoading] = useState(true);
  const [periodLabel, setPeriodLabel] = useState('');
  // Bulk-send of every invoice currently at status='pending'. Powers the
  // "Send all" button on the Financial Summary card; kept here (not in
  // a sub-component) so a successful send can immediately re-fetch the
  // stats and watch the counter drop.
  const [sendingAllPending, setSendingAllPending] = useState(false);
  const [pullingReport, setPullingReport] = useState(false);

  // On-demand EOD: send a fresh snapshot of today's Activity Report to
  // the current user's own email. Different from the 5:30 PM cron in
  // that it doesn't stamp the daily-guard, so the team-wide cron still
  // fires normally afterwards. Available to every authenticated role
  // (reps / OMs / admins) — anyone can pull their own copy any time.
  const pullOnDemandReport = async () => {
    setPullingReport(true);
    try {
      const res = await axios.post(
        `${API}/settings/eod-email/on-demand`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.status === 'error') {
        toast.error(res.data.message || 'Snapshot failed');
      } else {
        toast.success(res.data?.message || 'Snapshot sent to your inbox');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send snapshot');
    } finally {
      setPullingReport(false);
    }
  };

  // Check if user can see global data (admin/accounting) vs personal data (sales)
  const canSeeGlobalData = user?.role === 'admin' || user?.role === 'accounting' || user?.role === 'ops';
  const dataLabel = canSeeGlobalData ? 'Company' : 'Your';

  const fetchDashboardData = useCallback(async (startDate, endDate) => {
    try {
      setLoading(true);
      const params = {};
      if (startDate) params.start_date = `${startDate}T00:00:00`;
      if (endDate) params.end_date = `${endDate}T23:59:59`;

      // One request instead of five — and the server now sends only the six
      // recent loads rather than the whole book.
      const { data } = await axios.get(`${API}/dashboard/summary`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setStats(data.stats || {});
      setRecentLoads(data.recent_loads || []);
      setParsedEmails(data.parsed_emails || 0);
      setMyGoal(data.my_goal || null);
      setMyTeamGoal(data.team_goal || null);
    } catch (error) {
      toast.error('Failed to load dashboard data')
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  useEffect(() => {
    const defaultRange = getDateRange('ytd');
    setPeriodLabel(defaultRange.label);
    fetchDashboardData(defaultRange.start, defaultRange.end);
  }, [fetchDashboardData]);

  const sendAllPendingInvoices = async () => {
    setSendingAllPending(true);
    try {
      const { data } = await axios.post(
        `${API}/invoices/send-all-pending`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(data?.message || 'Pending invoices dispatched.');
      // Refresh stats so the counter drops without a manual reload
      const defaultRange = getDateRange('ytd');
      fetchDashboardData(defaultRange.start, defaultRange.end);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send pending invoices.');
    } finally {
      setSendingAllPending(false);
    }
  };

  const handleDateRangeChange = (start, end, label) => {
    setPeriodLabel(label);
    fetchDashboardData(start, end);
  };

  const statusConfig = {
    created: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Created' },
    tendered: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Tendered' },
    accepted: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Accepted' },
    picked_up: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Picked Up' },
    in_transit: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'In Transit' },
    delivered: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Delivered' },
    pod_received: { bg: 'bg-teal-100', text: 'text-teal-700', label: 'POD Received' },
    invoiced: { bg: 'bg-cyan-100', text: 'text-cyan-700', label: 'Invoiced' },
    paid: { bg: 'bg-green-100', text: 'text-green-700', label: 'Paid' }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-900 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Welcome & Quick Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <img src="/logo-medium.png" alt={company.company_name || 'TMS'} className="h-10 object-contain" />
          <div>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight" style={{fontFamily: 'Chivo, sans-serif'}}>Welcome back, {user?.name?.split(' ')[0]}</h2>
            <p className="text-sm text-slate-500 mt-0.5">Here's what's happening with your operations today.</p>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <Button
            size="sm"
            variant="outline"
            onClick={pullOnDemandReport}
            disabled={pullingReport}
            className="btn-press border-slate-300 hover:border-slate-400"
            data-testid="ondemand-eod-btn"
            title="Send a fresh snapshot of today's activity to your inbox — includes leads, loads, revenue, calls, emails and follow-ups up to right now."
          >
            {pullingReport ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Clock className="w-4 h-4 mr-1.5" />
            )}
            Email me today&apos;s activity
          </Button>
          <Link to="/loads">
            <Button size="sm" className="bg-red-600 hover:bg-red-700 shadow-lg shadow-red-500/25 btn-press">
              <Plus className="w-4 h-4 mr-1.5" />
              New Load
            </Button>
          </Link>
          <Link to="/email-parser">
            <Button size="sm" variant="outline" className="btn-press border-slate-300 hover:border-slate-400">
              <Mail className="w-4 h-4 mr-1.5" />
              Parse Email
            </Button>
          </Link>
        </div>
      </div>

      {/* Date Range Picker */}
      <div className="flex justify-end">
        <DateRangePicker onChange={handleDateRangeChange} />
      </div>

      {/* KPI Cards - Bento Grid. xl (not lg) for four-up: on a 1280-1440
          laptop with the sidebar open, four cards squeezed the margin card's
          view toggle past the right edge. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="border-slate-200 card-hover group" data-testid="total-loads-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{dataLabel} Loads</p>
                <p className="text-3xl font-bold text-slate-900 mt-1 tracking-tight" style={{fontFamily: 'Chivo, sans-serif'}}>{stats?.total_loads?.toLocaleString() || 0}</p>
                <p className="text-xs text-slate-400 mt-1">{periodLabel || 'YTD'}</p>
              </div>
              <div className="h-11 w-11 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25 group-hover:scale-110 transition-transform">
                <Truck className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 card-hover group" data-testid="active-loads-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Active</p>
                <p className="text-3xl font-bold text-slate-900 mt-1 tracking-tight" style={{fontFamily: 'Chivo, sans-serif'}}>{stats?.active_loads || 0}</p>
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                  In Progress
                </p>
              </div>
              <div className="h-11 w-11 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/25 group-hover:scale-110 transition-transform">
                <Clock className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 card-hover group" data-testid="total-revenue-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{dataLabel} Revenue</p>
                <p className="text-3xl font-bold text-slate-900 mt-1 tracking-tight" style={{fontFamily: 'Chivo, sans-serif'}}>${(stats?.total_revenue / 1000)?.toFixed(0) || 0}k</p>
                <p className="text-xs text-slate-400 mt-1">{periodLabel || 'YTD'}</p>
              </div>
              <div className="h-11 w-11 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/25 group-hover:scale-110 transition-transform">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 card-hover group relative overflow-hidden" data-testid="total-margin-card">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-600 via-red-500 to-transparent"></div>
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{dataLabel} Margin</p>
                <p className="text-3xl font-bold text-slate-900 mt-1 tracking-tight" style={{fontFamily: 'Chivo, sans-serif'}} data-testid="dashboard-margin-value">
                  ${(marginIncludesPipeline
                    ? ((stats?.total_margin || 0) + (stats?.pipeline_margin || 0)) / 1000
                    : (stats?.total_margin || 0) / 1000).toFixed(0)}k
                </p>
                {marginIncludesPipeline && (stats?.pipeline_margin || 0) > 0 ? (
                  <p className="text-[11px] text-amber-700 mt-1" data-testid="dashboard-margin-pipeline-note">
                    incl. ${((stats.pipeline_margin) / 1000).toFixed(0)}k pipeline
                  </p>
                ) : (
                  <p className="text-xs text-purple-600 mt-1">{periodLabel || 'YTD'} Profit</p>
                )}
                {myGoal?.goal > 0 && (
                  <div className="mt-2 max-w-[190px]" data-testid="dashboard-margin-goal">
                    <MarginGoalBar
                      goal={myGoal.goal}
                      recognised={myGoal.recognised_margin}
                      pipeline={myGoal.pipeline_margin}
                      includePipeline={marginIncludesPipeline}
                      compact
                      testId="dashboard-goal-bar"
                    />
                    <div className="text-[10px] text-slate-500 mt-0.5">this month's goal</div>
                  </div>
                )}
                {myTeamGoal?.goal > 0 && (
                  <div className="mt-2 max-w-[190px]" data-testid="dashboard-team-goal">
                    <MarginGoalBar
                      goal={myTeamGoal.goal}
                      recognised={myTeamGoal.recognised_margin}
                      pipeline={myTeamGoal.pipeline_margin}
                      includePipeline={marginIncludesPipeline}
                      compact
                      testId="dashboard-team-goal-bar"
                    />
                    <div className="text-[10px] text-indigo-600 mt-0.5">
                      my team's goal · {myTeamGoal.member_count} people
                    </div>
                  </div>
                )}
              </div>
              <div className="h-11 w-11 shrink-0 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
            </div>
            {/* Toggle sits on its own row — beside the value it overflowed the
                card on a laptop and clipped the icon. */}
            <div className="mt-3">
              <MarginViewToggle mode={marginMode} setMode={setMarginMode} short />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Agent margin — this month vs last month — with Recent Loads
            directly beneath it, so the left column matches the height of
            the stacked cards on the right instead of leaving a gap. */}
        <div className="lg:col-span-2 space-y-4">
          <AgentMarginPanel />

          <Card className="border-slate-200" data-testid="recent-loads-section">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold">Recent Loads</CardTitle>
                <Link to="/loads" className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
                  View all <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {recentLoads.length === 0 ? (
                <div className="text-center py-8">
                  <Truck className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">No loads yet</p>
                  <Link to="/loads">
                    <Button size="sm" className="mt-3">Create Load</Button>
                  </Link>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-6">
                  <div className="w-full min-w-[600px]">
                    {/* Header row */}
                    <div className="grid grid-cols-[80px_1fr_1.2fr_110px_110px] items-center gap-3 border-y border-slate-100 bg-slate-50/50 py-2 px-6">
                      <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider text-left">Load #</div>
                      <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider text-left">Customer</div>
                      <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider text-left">Route</div>
                      <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider text-left">Status</div>
                      <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider text-right">Rate</div>
                    </div>
                    {/* Data rows */}
                    {recentLoads.map((load, idx) => {
                      const status = statusConfig[load.status] || statusConfig.created;
                      return (
                        <RecentLoadRow
                          key={load.id}
                          load={load}
                          status={status}
                          isLast={idx === recentLoads.length - 1}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats & Actions */}
        <div className="space-y-4">
          {/* Pending Actions */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Link to="/email-parser" className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors group">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 bg-blue-100 rounded-md flex items-center justify-center">
                    <Mail className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Parse Emails</p>
                    <p className="text-xs text-slate-500">{parsedEmails} emails parsed</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
              </Link>
              
              <Link to="/customers?action=add" className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors group">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 bg-emerald-100 rounded-md flex items-center justify-center">
                    <Users className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Add Customer</p>
                    <p className="text-xs text-slate-500">Manage network</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
              </Link>
              
              <Link to="/carriers" className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors group">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 bg-purple-100 rounded-md flex items-center justify-center">
                    <Building2 className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Add Carrier</p>
                    <p className="text-xs text-slate-500">Build capacity</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
              </Link>
            </CardContent>
          </Card>

          {/* My Email Performance — sales-rep-focused widget. Backend
              scopes email queries to sent_by=current_user.id for
              non-admins, so this widget always shows the rep's own
              numbers even for admins (who use /email-analytics for the
              org-wide view). Placed above Financial Summary so it's the
              first thing a rep sees on log-in every morning. */}
          <MyEmailPerformanceWidget />

          {/* Financial Summary */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">Financial Summary <span className="text-xs font-normal text-slate-400">({periodLabel || 'YTD'})</span></CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Total Revenue</span>
                  <span className="text-sm font-semibold text-slate-900 mono">${stats?.total_revenue?.toLocaleString() || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Total Margin</span>
                  <span className="text-sm font-semibold text-emerald-600 mono">${stats?.total_margin?.toLocaleString() || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Margin %</span>
                  <span className="text-sm font-semibold text-slate-900 mono">
                    {stats?.total_revenue ? ((stats.total_margin / stats.total_revenue) * 100).toFixed(1) : 0}%
                  </span>
                </div>
                <div className="pt-2 border-t border-slate-100">
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className="text-sm text-slate-600 inline-flex items-center gap-1 cursor-help"
                      title="Invoices that have been generated in TMS but not yet emailed to the customer (status='pending'). Click 'Send all' to bulk-dispatch them; their status becomes 'sent' and the AR aging clock starts."
                    >
                      Invoices awaiting send
                      <span className="text-[10px] text-slate-400 font-normal">(ⓘ)</span>
                    </span>
                    <span className="text-sm font-semibold text-amber-600 mono" data-testid="dashboard-pending-invoices-count">
                      {stats?.pending_invoices || 0}
                    </span>
                  </div>
                  {/* One-click bulk-send. Admin/accounting only — backend
                      enforces the same role gate so the button is hidden
                      for sales reps. */}
                  {canSeeGlobalData && stats?.pending_invoices > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={sendAllPendingInvoices}
                      disabled={sendingAllPending}
                      className="w-full mt-2 h-8 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid="send-all-pending-invoices-btn"
                    >
                      {sendingAllPending
                        ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        : <Send className="h-3.5 w-3.5 mr-1.5" />}
                      {sendingAllPending
                        ? 'Sending…'
                        : `Send all ${stats?.pending_invoices} now`}
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
